import { useCallback, useEffect, useState } from "react";
import "./Reward.css";

// 기프티쇼 전체 상품(2천 개 넘음)을 찾아보는 칸. 학원(상점에 올리기)과 본사(추천·숨김)가 같이 쓴다.
// 한 번에 다 받지 않고 60개씩 받는다(서버: reward/views.py browse_product_page).

const formatNumber = (value) => (value ?? 0).toLocaleString("ko-KR");

const PRICE_OPTION_LIST = [
  { value: "", label: "가격 전체" },
  { value: "3000", label: "3천원 이하" },
  { value: "5000", label: "5천원 이하" },
  { value: "10000", label: "1만원 이하" },
  { value: "30000", label: "3만원 이하" },
];

const EMPTY_FILTER = { q: "", brand: "", category: "", max_price: "", only: "" };

// renderBrandActions(brand, reload): 브랜드를 골랐을 때 그 브랜드 전체에 하는 일(올리기·추천 등)
const ProductBrowser = ({ fetchPage, onlyOptionList, renderProduct, renderBrandActions, reloadKey, onLoaded }) => {
  const [filter, setFilter] = useState(EMPTY_FILTER);
  const [keyword, setKeyword] = useState("");
  const [data, setData] = useState(null);
  const [productList, setProductList] = useState([]);
  const [message, setMessage] = useState("");

  const load = useCallback(
    async (page) => {
      try {
        const response = await fetchPage({ ...filter, page });
        setData(response);
        setProductList((previous) => (page === 1 ? response.product_list : [...previous, ...response.product_list]));
        if (onLoaded) onLoaded(response);
        setMessage("");
      } catch {
        setMessage("상품을 불러오지 못했어요.");
      }
    },
    [fetchPage, filter, onLoaded]
  );

  useEffect(() => {
    load(1);
  }, [load, reloadKey]);

  const change = (key, value) => setFilter((previous) => ({ ...previous, [key]: value }));
  // 목록 안의 한 상품만 바꾼다(올리기·추천·숨김). 처음부터 다시 받으면 보던 자리를 잃는다
  const updateProduct = (product, patch) => {
    setProductList((previous) => previous.map((one) => (one.id === product.id ? { ...one, ...patch } : one)));
    // 올리고 내린 만큼 브랜드 칸의 '올린 수' 도 맞춘다
    if ("is_selected" in patch && patch.is_selected !== product.is_selected) {
      const selectedDifference = patch.is_selected ? 1 : -1;
      setData((previous) => ({
        ...previous,
        brand_list: previous.brand_list.map((brand) =>
          brand.name === product.brand_name ? { ...brand, selected_count: brand.selected_count + selectedDifference } : brand
        ),
      }));
    }
  };

  return (
    <div>
      <form
        className="reward-search"
        onSubmit={(event) => {
          event.preventDefault();
          change("q", keyword.trim());
        }}
      >
        <input value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="상품명 · 브랜드" />
        <button type="submit" className="reward-button reward-button--ghost">
          찾기
        </button>
      </form>
      <div className="reward-filter">
        <select value={filter.category} onChange={(event) => change("category", event.target.value)}>
          <option value="">분류 전체</option>
          {(data?.category_list || []).map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        <select value={filter.max_price} onChange={(event) => change("max_price", event.target.value)}>
          {PRICE_OPTION_LIST.map((one) => (
            <option key={one.value} value={one.value}>
              {one.label}
            </option>
          ))}
        </select>
        {onlyOptionList.map((one) => (
          <button
            type="button"
            key={one.value}
            className="reward-chip"
            aria-pressed={filter.only === one.value}
            onClick={() => change("only", filter.only === one.value ? "" : one.value)}
          >
            {one.label}
          </button>
        ))}
      </div>
      {/* 브랜드를 로고로 늘어놓는다. 누르면 그 브랜드 상품만 사진으로 펼쳐진다 */}
      <div className="reward-brand-strip">
        <button type="button" className="reward-brand-tile" aria-pressed={!filter.brand} onClick={() => change("brand", "")}>
          <span className="reward-brand-tile__icon">전체</span>
          <span className="reward-brand-tile__name">브랜드 전체</span>
        </button>
        {(data?.brand_list || []).map((brand) => (
          <button
            type="button"
            key={brand.name}
            className="reward-brand-tile"
            aria-pressed={filter.brand === brand.name}
            onClick={() => change("brand", filter.brand === brand.name ? "" : brand.name)}
          >
            <span className="reward-brand-tile__icon">
              {brand.icon_url ? <img src={brand.icon_url} alt="" loading="lazy" /> : brand.name.slice(0, 2)}
            </span>
            <span className="reward-brand-tile__name">{brand.name}</span>
            <span className="reward-brand-tile__count">
              {brand.selected_count > 0 ? `${brand.selected_count}/${brand.count}` : brand.count}
            </span>
          </button>
        ))}
      </div>
      {filter.brand && renderBrandActions && (
        <div className="reward-brand-actions">
          <b>{filter.brand}</b>
          {renderBrandActions(
            (data?.brand_list || []).find((one) => one.name === filter.brand) || { name: filter.brand, count: 0, selected_count: 0 },
            () => load(1)
          )}
        </div>
      )}
      {message && <div className="reward-message">{message}</div>}
      {data && <div className="reward-count">{formatNumber(data.total_count)}개</div>}
      {data && productList.length === 0 && <div className="reward-empty">맞는 상품이 없어요.</div>}
      <div className="reward-product-grid">
        {productList.map((product) => renderProduct(product, (patch) => updateProduct(product, patch)))}
      </div>
      {data?.has_next && (
        <div className="reward-actions" style={{ justifyContent: "center" }}>
          <button type="button" className="reward-button reward-button--ghost" onClick={() => load(data.page + 1)}>
            더 보기
          </button>
        </div>
      )}
    </div>
  );
};

export const ProductImage = ({ product, children }) => (
  <span className="reward-product__image">
    {product.image_url ? <img src={product.image_url} alt="" loading="lazy" /> : <span>{product.brand_name}</span>}
    {children}
  </span>
);

export const productPrice = (product) => `${formatNumber(product.point_price)}P`;

export default ProductBrowser;
